import React, { FunctionComponent, useContext } from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormFields, FormTextField, Stack, getById } from "sprint-common-web";

import { FramerateProfileListingContext } from "../../../context/FramerateProfilesListingContext";
import { useEditFramerateProfileForm } from "./useEditFramerateProfileForm";

export interface EditFramerateProfileFormProps {
    itemId: string;
    onCancel(): void;
    onSubmit(): void;
}

export interface EditFramerateProfileFormInputs {
    name: string;
    framerate2398: string;
    framerate2997: string;
    framerate24: string;
    framerate25: string;
    framerate30: string;
    framerate50: string;
    framerate5994: string;
    framerate60: string;
    framerateOther: string;
}

export const EditFramerateProfileForm: FunctionComponent<EditFramerateProfileFormProps> = (
    props: EditFramerateProfileFormProps
) => {
    const { itemId, onCancel, onSubmit } = props;
    const { items } = useContext(FramerateProfileListingContext);
    const { editFramerateProfile } = useEditFramerateProfileForm();
    const item = getById(items, itemId);
    const { errors, formState, control, reset, handleSubmit } = useForm<EditFramerateProfileFormInputs>({
        defaultValues: {
            name: item.name,
            framerate2398: item.mappings[23.98],
            framerate2997: item.mappings[29.97],
            framerate24: item.mappings[24],
            framerate25: item.mappings[25],
            framerate30: item.mappings[30],
            framerate50: item.mappings[50],
            framerate5994: item.mappings[59.94],
            framerate60: item.mappings[60],
            framerateOther: item.mappings["OTHER"]
        },
        criteriaMode: "all"
    });

    const handleEditFramerateProfile = async (inputs: EditFramerateProfileFormInputs) => {
        await editFramerateProfile(props.itemId, inputs);
        onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleEditFramerateProfile)}>
                <Stack verticalAlign="space-between">
                    <FormFields>
                        <FormTextField
                            label="Name"
                            control={control}
                            name="name"
                            errors={errors}
                            rules={{
                                required: "Name is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 23.976"
                            control={control}
                            name="framerate2398"
                            errors={errors}
                            rules={{
                                required: "Framerate 23.976 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 24"
                            control={control}
                            name="framerate24"
                            errors={errors}
                            rules={{
                                required: "Framerate 24 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 25"
                            control={control}
                            name="framerate25"
                            errors={errors}
                            rules={{
                                required: "Framerate 25 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 29.97"
                            control={control}
                            name="framerate2997"
                            errors={errors}
                            rules={{
                                required: "Framerate 29.97 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 30"
                            control={control}
                            name="framerate30"
                            errors={errors}
                            rules={{
                                required: "Framerate 30 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 50"
                            control={control}
                            name="framerate50"
                            errors={errors}
                            rules={{
                                required: "Framerate 50 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 59.94"
                            control={control}
                            name="framerate5994"
                            errors={errors}
                            rules={{
                                required: "Framerate 59.94 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate 60"
                            control={control}
                            name="framerate60"
                            errors={errors}
                            rules={{
                                required: "Framerate 60 is required field"
                            }}
                        />
                        <FormTextField
                            label="Framerate Other"
                            control={control}
                            name="framerateOther"
                            errors={errors}
                            tooltipContent="Framerate value that does not fit in standard framerates. Works as a fallback framerate."
                            rules={{
                                required: "Framerate Other is required field"
                            }}
                        />
                    </FormFields>
                    <FormActions
                        isLoading={formState.isSubmitting || formState.isValidating}
                        onCancel={handleCancelButtonClick}
                    />
                </Stack>
            </Form>
        </Stack>
    );
};
